import type { MerchantApiClient } from "../../types.js";
import type {
  ManualMappingDraft,
  MappingDecisionDraft,
  MappingFilters,
  MappingPage,
  ProductMapping,
} from "./models.js";

interface DataEnvelope<T> {
  data: T;
  meta: { requestId: string };
}

interface ListEnvelope {
  items: ProductMapping[];
  nextCursor: string | null;
  meta: { requestId: string };
}

export interface MappingsApi {
  createMapping(draft: ManualMappingDraft, idempotencyKey: string): Promise<ProductMapping>;
  getMapping(productId: string, mappingId: string): Promise<ProductMapping>;
  listMappings(filters: MappingFilters): Promise<MappingPage>;
  patchMapping(
    mapping: ProductMapping,
    draft: MappingDecisionDraft,
    idempotencyKey: string,
  ): Promise<ProductMapping>;
}

export function createMappingsApi(client: MerchantApiClient): MappingsApi {
  return {
    async createMapping(draft, idempotencyKey) {
      return unwrap(
        await client.post<DataEnvelope<ProductMapping>>("/v1/api/mappings", draft, {
          idempotencyKey,
        }),
      );
    },
    async getMapping(productId, mappingId) {
      return unwrap(
        await client.get<DataEnvelope<ProductMapping>>(
          `/v1/api/mappings/${encodeURIComponent(mappingId)}?productId=${encodeURIComponent(productId)}`,
        ),
      );
    },
    async listMappings(filters) {
      const envelope = await client.get<ListEnvelope>(`/v1/api/mappings?${query(filters)}`);
      return { mappings: envelope.items, nextCursor: envelope.nextCursor };
    },
    async patchMapping(mapping, draft, idempotencyKey) {
      return unwrap(
        await client.patch<DataEnvelope<ProductMapping>>(
          `/v1/api/mappings/${encodeURIComponent(mapping.mappingId)}`,
          {
            platformProductId: mapping.platformProductId,
            action: draft.action,
            ...(draft.useDirectStatus === true ? { status: draft.status } : {}),
            reason: draft.reason,
            canonicalUrl: draft.canonicalUrl,
            merchantNote: draft.merchantNote,
            overrideHardConflict: draft.overrideHardConflict,
            verifiedAttributes: draft.verifiedAttributes,
          },
          { idempotencyKey },
        ),
      );
    },
  };
}

function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}

function query(filters: MappingFilters): string {
  return new URLSearchParams(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => [key, String(value)]),
  ).toString();
}
